// src/utils/qrCodeUtils.ts
import QRCode from 'qrcode';

export type QRCodeOptions = {
    /** Display size in CSS px (e.g. 200). Defaults to 200. */
    logicalSize?: number;
    /** Pixel multiplier: 1 = normal, 2 = HD/retina. Defaults to 2. */
    scale?: number;
    fgColor?: string;
    bgColor?: string;
    includeMargin?: boolean;
    errorCorrectionLevel?: 'L' | 'M' | 'Q' | 'H';
    /** Optional logo as data URL or http(s) URL with CORS. */
    logoSrc?: string | null;
    /** Logo size relative to QR area; 0.2 => 20%. */
    logoScale?: number;
    /**
     * Force a square canvas of this exact pixel size (width = height).
     * The QR area shrinks so labels fit inside the fixed boundary.
     * Use 600 for 2"×2" at 300 PPI (print-ready sticker).
     */
    fixedCanvasSize?: number;
    label?: {
        /** Label text. */
        text?: string;
        /** Label font family. */
        fontFamily?: string;
        /** Label font color. */
        fontColor?: string;
        /** Label font size in CSS px. */
        fontSize?: number;
        /** Label line spacing in CSS px. */
        lineSpacing?: number;
        /** Label padding top in CSS px. */
        paddingTop?: number;
        /** Label padding right in CSS px. */
        paddingRight?: number;
        /** Label padding bottom in CSS px. */
        paddingBottom?: number;
        /** Label padding left in CSS px. */
        paddingLeft?: number;
        /** Label lines. */
        lines?: string[];
        /** Label alignment: 'left' | 'center' | 'right'. Default: 'center' */
        align?: 'left' | 'center' | 'right';
    };
};

/**
 * Internal: render a QR to an offscreen canvas and return it.
 */
export async function renderQRCodeToCanvas(
    text: string,
    options: QRCodeOptions = {},
): Promise<HTMLCanvasElement> {
    const {
        logicalSize = 200,
        scale = 2,
        fgColor = '#000000',
        bgColor = '#ffffff',
        includeMargin = true,
        errorCorrectionLevel = 'M',
        logoSrc,
        logoScale = 0.2,
        fixedCanvasSize,
        label,
    } = options;

    const pixelQRSize = Math.round(logicalSize * scale);

    /* ---------- Label calculations ---------- */
    const fontSize = (label?.fontSize ?? 10) * scale;
    const lineSpacing = (label?.lineSpacing ?? 4) * scale;
    const paddingTop = (label?.paddingTop ?? 8) * scale;

    const labelLines = label?.lines ?? [];
    const labelHeight =
        labelLines.length > 0
            ? paddingTop +
              labelLines.length * fontSize +
              (labelLines.length - 1) * lineSpacing
            : 0;

    /*
     * fixedCanvasSize: forces a square canvas (width = height = fixedCanvasSize).
     * The QR area shrinks by labelHeight so labels stay within the sticker boundary.
     * Without fixedCanvasSize the canvas extends below the QR to fit the label.
     *
     * 2"×2" sticker at 300 PPI → fixedCanvasSize: 600
     * 2"×2" sticker at 96 PPI  → fixedCanvasSize: 192
     */
    const canvasW = fixedCanvasSize ?? pixelQRSize;
    const canvasH = fixedCanvasSize ?? pixelQRSize + labelHeight;
    const qrAreaSize = fixedCanvasSize
        ? fixedCanvasSize - labelHeight
        : pixelQRSize;

    /* ---------- Final canvas ---------- */
    const canvas = document.createElement('canvas');
    canvas.width = canvasW;
    canvas.height = canvasH;

    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas context not available');

    ctx.fillStyle = bgColor;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    /* ---------- QR canvas ---------- */
    const qrCanvas = document.createElement('canvas');
    qrCanvas.width = qrAreaSize;
    qrCanvas.height = qrAreaSize;

    await QRCode.toCanvas(qrCanvas, text, {
        errorCorrectionLevel,
        margin: includeMargin ? 1 : 0,
        color: { dark: fgColor, light: bgColor },
        width: qrAreaSize,
    });

    // Centre the QR horizontally when canvas is wider than the QR area
    const qrOffsetX = Math.round((canvasW - qrAreaSize) / 2);
    ctx.drawImage(qrCanvas, qrOffsetX, 0);

    /* ---------- Optional logo ---------- */
    if (logoSrc) {
        const img = new Image();
        img.crossOrigin = 'anonymous';

        await new Promise<void>((resolve, reject) => {
            img.onload = () => resolve();
            img.onerror = () => reject();
            img.src = logoSrc;
        });

        const logoPx = Math.round(qrAreaSize * logoScale);
        const x = qrOffsetX + (qrAreaSize - logoPx) / 2;
        const y = (qrAreaSize - logoPx) / 2;

        // White circular background (scanner-safe)
        ctx.save();
        ctx.beginPath();
        ctx.fillStyle = bgColor;
        ctx.arc(
            qrOffsetX + qrAreaSize / 2,
            qrAreaSize / 2,
            logoPx / 2 + 6 * scale,
            0,
            Math.PI * 2,
        );
        ctx.fill();
        ctx.restore();

        ctx.drawImage(img, x, y, logoPx, logoPx);
    }

    /* ---------- Draw label text ---------- */
    if (labelLines.length > 0) {
        ctx.fillStyle = label?.fontColor ?? '#000';
        ctx.font = `${fontSize}px ${label?.fontFamily ?? 'Nunito'}`;
        ctx.textBaseline = 'top';

        labelLines.forEach((line, index) => {
            let x: number;

            if (label?.align === 'left') {
                x = 0;
                ctx.textAlign = 'left';
            } else if (label?.align === 'right') {
                x = canvasW;
                ctx.textAlign = 'right';
            } else {
                x = canvasW / 2;
                ctx.textAlign = 'center';
            }

            const y = qrAreaSize + paddingTop + index * (fontSize + lineSpacing);
            ctx.fillText(line, x, y);
        });
    }

    return canvas;
}

/**
 * Generate a PNG data URL (suitable for <img src="..."> or download).
 */
export async function generateQRCodeDataUrl(
    text: string,
    options: QRCodeOptions = {},
): Promise<string> {
    const canvas = await renderQRCodeToCanvas(text, options);
    return canvas?.toDataURL('image/png');
}

/**
 * Generate a PNG Blob (good for uploading, file-saver, etc).
 */
export async function generateQRCodeBlob(
    text: string,
    options: QRCodeOptions = {},
): Promise<Blob | null> {
    const canvas = await renderQRCodeToCanvas(text, options);
    return await new Promise((resolve) =>
        canvas.toBlob((blob) => resolve(blob), 'image/png'),
    );
}

/**
 * Simple helper: download any data URL as a file.
 */
export function downloadDataUrlAsFile(dataUrl: string, filename: string) {
    const link = document.createElement('a');
    link.href = dataUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
}

/**
 * Open a print window for a given image data URL (PNG/SVG).
 * Uses the browser's print dialog to get high-quality print.
 */
export function openPrintWindowFromDataUrl(
    dataUrl: string,
    opts?: { title?: string },
) {
    const title = opts?.title ?? 'Print QR';
    const printWindow = window.open('', '_blank', 'noopener,noreferrer');

    if (!printWindow) {
        alert('Please allow popups to print.');
        return;
    }

    const html = `<!doctype html>
<html>
<head>
<meta charset="utf-8" />
<title>${title}</title>
<style>
  html,body{height:100%; margin:0;}
  .wrap{display:flex; align-items:center; justify-content:center; height:100%;}
  img{display:block; max-width:100%; height:auto;}
  @media print {
    img { -webkit-print-color-adjust: exact; }
    @page { margin: 10mm; }
  }
</style>
</head>
<body>
  <div class="wrap">
    <img src="${dataUrl}" alt="QR code" />
  </div>
  <script>
    setTimeout(function(){ window.focus(); window.print(); }, 250);
  </script>
</body>
</html>`;

    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
}
// src/utils/qrCodeUtils.ts

export function openPrintWindowFromDataUrlV2(
    dataUrl: string,
    opts?: {
        title?: string;
        /**
         * Print as a fixed-size sticker (e.g. '2in').
         * Sets @page size to width×height with zero margins so the image
         * fills exactly the sticker area on the printer.
         */
        stickerSize?: string;
    },
) {
    const title = opts?.title ?? 'Print QR';
    const sz = opts?.stickerSize;

    // Sticker mode: @page matches the physical sticker; no margins; image fills it exactly.
    // Default mode: centred on the page with 10mm margins.
    const pageStyle = sz
        ? `@page { size: ${sz} ${sz}; margin: 0; }
           html, body { margin: 0; padding: 0; width: ${sz}; height: ${sz}; overflow: hidden; }
           img { display: block; width: ${sz}; height: ${sz};
                 -webkit-print-color-adjust: exact; print-color-adjust: exact; }`
        : `html, body { height: 100%; margin: 0; }
           .wrap { display: flex; align-items: center; justify-content: center; height: 100%; }
           img { display: block; max-width: 100%; height: auto;
                 -webkit-print-color-adjust: exact; }
           @media print { @page { margin: 10mm; } }`;

    // Create a hidden iframe
    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    iframe.style.visibility = 'hidden';

    document.body.appendChild(iframe);

    const doc = iframe.contentDocument || iframe.contentWindow?.document;
    if (!doc) {
        console.error('Unable to access iframe document for printing');
        document.body.removeChild(iframe);
        return;
    }

    const html = `<!doctype html>
  <html>
  <head>
  <meta charset="utf-8" />
  <title>${title}</title>
  <style>
    ${pageStyle}
  </style>
  </head>
  <body>
    ${sz ? '' : '<div class="wrap">'}
      <img src="${dataUrl}" alt="QR code" />
    ${sz ? '' : '</div>'}
  </body>
  </html>`;

    doc.open();
    doc.write(html);
    doc.close();

    const win = iframe.contentWindow;
    if (!win) {
        console.error('Unable to access iframe window for printing');
        document.body.removeChild(iframe);
        return;
    }

    // Give the iframe a moment to render, then print
    setTimeout(() => {
        win.focus();
        win.print();

        // Remove iframe after printing (small delay so some browsers finish printing)
        setTimeout(() => {
            document.body.removeChild(iframe);
        }, 500);
    }, 300);
}
